import React, { useState } from 'react';
import {
  FormGroup,
  InputLabel,
  DialogTitle,
  DialogContent,
  FormControl,
  Select,
  Dialog,
  MenuItem,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  Box,
} from '@mui/material';
import PostAdd from '@mui/icons-material/PostAdd';
import FilterAltOutlined from '@mui/icons-material/FilterAltOutlined';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';

import TranscriptImages from '../../icons/TranscriptImages';
import TranscriptChatIcon from '../../icons/TranscriptChatIcon';
import CircularProgress from '@mui/material/CircularProgress';
import {
  RestartAltOutlined,
  FileDownloadOutlined,
  FileUploadOutlined,
} from '@mui/icons-material';
import { IUserSettings, ITranscriptSegment } from '../../global/interfaces';
import ToggleButton from '../ToggleButton';

const modelList: string[] = [
  'Haiku 3',
  'Sonnet 3.5',
  'Sonnet 3.7',
  'Sonnet 4',
  'GPT-4',
  'GPT-4o',
  'GPT-4o-mini',
];

interface IncludeOptionsProps {
  hasImages: boolean;
  hasNotes: boolean;
  hideImages: boolean;
  setHideImages: (value: boolean) => void;
  hideNotes: boolean;
  setHideNotes: (value: boolean) => void;
  hideAIResults: boolean;
  setHideAIResults: (value: boolean) => void;
  hideFiltedText: boolean | null;
  setHideFiltedText: (value: boolean) => void;
  setRegenerateIndex?: (value: number | undefined) => void;
  selectedModel: string;
  setSelectedModel: (value: string) => void;
  isGeneratingResponse: boolean;
  handleDownloadTranscript: () => void;
  transcriptSegments: ITranscriptSegment[];
  allCollapsed: boolean;
  toggleCollapseAll: () => void;
  handleConfirmReset: () => void;
  userSettings: IUserSettings;
  setOpenUploadModal: (value: boolean) => void;
  styles: Record<string, any>;
}

const IncludeOptions: React.FC<IncludeOptionsProps> = ({
  hasImages,
  hasNotes,
  hideImages,
  setHideImages,
  hideNotes,
  setHideNotes,
  hideAIResults,
  setHideAIResults,
  hideFiltedText,
  setHideFiltedText,
  selectedModel,
  setSelectedModel,
  setRegenerateIndex,
  isGeneratingResponse,
  handleDownloadTranscript,
  transcriptSegments,
  userSettings,
  handleConfirmReset,
  setOpenUploadModal,
  styles,
}) => {
  const [openAIDialog, setOpenAIDialog] = useState(false);

  const handleHideAIResults = () => {
    if (!hideAIResults) {
      setHideAIResults(true);
      return;
    }

    if (setRegenerateIndex) {
      setOpenAIDialog(true);
      setRegenerateIndex(undefined);
    }

    setHideAIResults(false);
    setSelectedModel('');
  };

  return (
    <>
      {openAIDialog && (
        <Dialog
          open={openAIDialog}
          onClose={() => setOpenAIDialog(false)}
          maxWidth="sm"
          fullWidth>
          <DialogTitle>AI Analysis Options</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Select AI Model</InputLabel>
              <Select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                label="Select Model">
                {modelList.map((model) => (
                  <MenuItem value={model}>{model}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button
              disabled={!selectedModel}
              onClick={() => {
                setRegenerateIndex?.(-1);
                setOpenAIDialog(false);
              }}>
              Run New Analysis
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setOpenAIDialog(false);
              }}>
              Show Previous Results
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <FormGroup row sx={styles.iconTray}>
        {hasImages && (
          <ToggleButton
            label="Images"
            checked={hideImages}
            onChange={setHideImages}
            icon={<TranscriptImages />}
            styles={{ marginLeft: '1rem', marginRight: 0 }}
            nor={true}
          />
        )}

        {hasNotes && (
          <ToggleButton
            label="Notes"
            checked={hideNotes}
            onChange={setHideNotes}
            icon={<PostAdd />}
            styles={{ marginLeft: '1rem', marginRight: 0 }}
            nor={true}
          />
        )}

        {userSettings?.dev && ( //only show this if there are AI result attachments
          <Tooltip
            title={hideAIResults ? 'Show AI Results' : 'Hide AI Results'}>
            <IconButton
              onClick={() => !isGeneratingResponse && handleHideAIResults()}
              sx={{
                ...styles.trayButton,
                marginRight: '0',
                marginLeft: '1rem',
                border: `.1rem solid ${hideAIResults ? '#d3d3d3' : 'orange'}`,
              }}>
              {isGeneratingResponse ? (
                <CircularProgress size={20} />
              ) : (
                <AutoAwesomeOutlined />
              )}
            </IconButton>
          </Tooltip>
        )}

        {false && hideFiltedText !== null && (
          <IconButton
            onClick={() => setHideFiltedText(!hideFiltedText)}
            sx={{
              ...styles.trayButton,
              border: `.1rem solid ${hideFiltedText ? 'orange' : '#d3d3d3'}`,
            }}>
            <FilterAltOutlined />
          </IconButton>
        )}
        <Box sx={{ flexGrow: 1 }} />

        {transcriptSegments?.length > 0 && (
          <>
            <Tooltip title={'Export Transcript'}>
              <IconButton
                sx={{ ...styles.trayButton, marginLeft: 'auto' }}
                onClick={handleDownloadTranscript}>
                <FileDownloadOutlined sx={{ fontSize: '1.5rem' }} />
              </IconButton>
            </Tooltip>

            <Tooltip title={'Reset Transcript'}>
              <IconButton sx={styles.trayButton} onClick={handleConfirmReset}>
                <RestartAltOutlined sx={{ fontSize: '1.5rem' }} />
              </IconButton>
            </Tooltip>
          </>
        )}
        {transcriptSegments?.length === 0 && (
          <Tooltip title={'Upload File'}>
            <IconButton
              sx={styles.trayButton}
              onClick={() => setOpenUploadModal(true)}>
              <FileUploadOutlined sx={{ fontSize: '1.5rem' }} />
            </IconButton>
          </Tooltip>
        )}

        {false && (
          <IconButton
            onClick={() => setHideImages(!hideImages)}
            sx={{
              ...styles.trayButton,
              border: `.1rem solid ${hideImages ? '#d3d3d3' : 'orange'}`,
            }}>
            <TranscriptChatIcon />
          </IconButton>
        )}
      </FormGroup>
    </>
  );
};

export default IncludeOptions;
